import * as React from "react";
import { createPortal } from "react-dom";
import { ChevronRight } from "lucide-react";

import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";

interface DropdownMenuContextValue {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  triggerRef: React.RefObject<HTMLElement | null>;
  registerSubContent: (ref: React.RefObject<HTMLElement | null>) => void;
  unregisterSubContent: (ref: React.RefObject<HTMLElement | null>) => void;
  subContentRefs: React.RefObject<Set<React.RefObject<HTMLElement | null>>>;
  isMobile: boolean;
}

const DropdownMenuContext = React.createContext<DropdownMenuContextValue | null>(null);

function useDropdownMenu() {
  const context = React.useContext(DropdownMenuContext);
  if (!context) {
    throw new Error("DropdownMenu components must be used within DropdownMenu");
  }
  return context;
}

// Submenu context
interface DropdownMenuSubContextValue {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  closeImmediately: () => void;
  triggerRef: React.RefObject<HTMLElement | null>;
}

const DropdownMenuSubContext = React.createContext<DropdownMenuSubContextValue | null>(null);

function useDropdownMenuSub() {
  const context = React.useContext(DropdownMenuSubContext);
  if (!context) {
    throw new Error("DropdownMenuSub components must be used within DropdownMenuSub");
  }
  return context;
}

const DropdownMenu = ({ children, onOpenChange }: { children: React.ReactNode; onOpenChange?: (open: boolean) => void }) => {
  const [open, setOpen] = React.useState(false);
  const triggerRef = React.useRef<HTMLElement | null>(null);
  const subContentRefs = React.useRef<Set<React.RefObject<HTMLElement | null>>>(new Set());
  const isMobile = useIsMobile();

  const registerSubContent = React.useCallback((ref: React.RefObject<HTMLElement | null>) => {
    subContentRefs.current.add(ref);
  }, []);

  const unregisterSubContent = React.useCallback((ref: React.RefObject<HTMLElement | null>) => {
    subContentRefs.current.delete(ref);
  }, []);

  const handleSetOpen: React.Dispatch<React.SetStateAction<boolean>> = (value) => {
    setOpen((prev) => {
      const next = typeof value === "function" ? value(prev) : value;
      onOpenChange?.(next);
      return next;
    });
  };

  return (
    <DropdownMenuContext.Provider value={{ open, setOpen: handleSetOpen, triggerRef, registerSubContent, unregisterSubContent, subContentRefs, isMobile }}>
      <div className="relative inline-block text-left">{children}</div>
    </DropdownMenuContext.Provider>
  );
};

interface DropdownMenuTriggerProps {
  asChild?: boolean;
  children: React.ReactNode;
}

const DropdownMenuTrigger = React.forwardRef<
  HTMLButtonElement,
  DropdownMenuTriggerProps & React.ButtonHTMLAttributes<HTMLButtonElement>
>(({ asChild, children, onClick, ...props }, ref) => {
  const { open, setOpen, triggerRef } = useDropdownMenu();

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    setOpen(!open);
    onClick?.(e);
  };

  const setRefs = React.useCallback(
    (node: HTMLButtonElement | null) => {
      triggerRef.current = node;
      if (typeof ref === "function") {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    },
    [ref, triggerRef]
  );

  if (asChild && React.isValidElement(children)) {
    const childProps = (children as React.ReactElement<Record<string, unknown>>).props;
    const childOnClick = childProps.onClick as ((e: React.MouseEvent) => void) | undefined;
    return React.cloneElement(children as React.ReactElement<Record<string, unknown>>, {
      ref: (node: HTMLElement | null) => {
        triggerRef.current = node;
        if (typeof ref === "function") {
          ref(node as HTMLButtonElement | null);
        } else if (ref) {
          (ref).current = node as HTMLButtonElement;
        }
      },
      onClick: (e: React.MouseEvent) => {
        childOnClick?.(e);
        onClick?.(e as React.MouseEvent<HTMLButtonElement>);
        setOpen(!open);
      },
      "aria-haspopup": "menu" as const,
      "aria-expanded": open,
    });
  }

  return (
    <button ref={setRefs} onClick={handleClick} aria-haspopup="menu" aria-expanded={open} {...props}>
      {children}
    </button>
  );
});
DropdownMenuTrigger.displayName = "DropdownMenuTrigger";

interface DropdownMenuContentProps extends React.HTMLAttributes<HTMLDivElement> {
  align?: "start" | "center" | "end";
  side?: "top" | "bottom";
}

const DropdownMenuContent = React.forwardRef<HTMLDivElement, DropdownMenuContentProps>(
  ({ className, align = "center", side = "bottom", children, ...props }, ref) => {
    const { open, setOpen, triggerRef, subContentRefs, isMobile } = useDropdownMenu();
    const contentRef = React.useRef<HTMLDivElement>(null);
    const [position, setPosition] = React.useState<{ top: number; left: number }>({ top: 0, left: 0 });

    React.useEffect(() => {
      if (open && triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        let top: number;
        let left: number;

        if (isMobile) {
          // Fixed positioning — use viewport coords directly
          if (side === "top") {
            top = rect.top;
          } else {
            top = rect.bottom + 4;
          }

          if (align === "end") {
            left = rect.right;
          } else if (align === "start") {
            left = rect.left;
          } else {
            left = rect.left + rect.width / 2;
          }
        } else {
          if (side === "top") {
            top = rect.top + window.scrollY;
          } else {
            top = rect.bottom + window.scrollY + 4;
          }

          if (align === "end") {
            left = rect.right + window.scrollX;
          } else if (align === "start") {
            left = rect.left + window.scrollX;
          } else {
            left = rect.left + window.scrollX + rect.width / 2;
          }
        }

        setPosition({ top, left });
      }
    }, [open, align, side, triggerRef, isMobile]);

    React.useEffect(() => {
      const handleClickOutside = (event: PointerEvent) => {
        const target = event.target as Node;
        
        // Check if click is inside main content or trigger
        if (contentRef.current?.contains(target) || triggerRef.current?.contains(target)) {
          return;
        }
        
        // Check if click is inside any submenu content
        for (const subRef of subContentRefs.current) {
          if (subRef.current?.contains(target)) {
            return;
          }
        }
        
        setOpen(false);
      };

      if (open) {
        document.addEventListener("pointerdown", handleClickOutside);
      }

      return () => {
        document.removeEventListener("pointerdown", handleClickOutside);
      };
    }, [open, setOpen, triggerRef, subContentRefs]);

    // Keyboard navigation handler
    const handleKeyDown = React.useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
      const items = contentRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]');
      if (!items || items.length === 0) return;

      const currentIndex = Array.from(items).findIndex((item) => item === document.activeElement);

      switch (e.key) {
        case "Escape":
          e.preventDefault();
          setOpen(false);
          break;
        case "ArrowDown":
          e.preventDefault();
          items[currentIndex < items.length - 1 ? currentIndex + 1 : 0]?.focus();
          break;
        case "ArrowUp":
          e.preventDefault();
          items[currentIndex > 0 ? currentIndex - 1 : items.length - 1]?.focus();
          break;
        case "Enter":
        case " ":
          e.preventDefault();
          if (document.activeElement instanceof HTMLElement) {
            document.activeElement.click();
          }
          break;
      }
    }, [setOpen]);

    // Focus first menu item when menu opens
    React.useEffect(() => {
      if (open) {
        requestAnimationFrame(() => {
          const firstItem = contentRef.current?.querySelector<HTMLElement>('[role="menuitem"]');
          firstItem?.focus();
        });
      }
    }, [open]);

    // Return focus to trigger when menu closes
    const wasOpen = React.useRef(false);
    React.useEffect(() => {
      if (wasOpen.current && !open) {
        triggerRef.current?.focus();
      }
      wasOpen.current = open;
    }, [open, triggerRef]);

    if (!open) return null;

    return createPortal(
      <div
        ref={(node) => {
          contentRef.current = node;
          if (typeof ref === "function") {
            ref(node);
          } else if (ref) {
            ref.current = node;
          }
        }}
        style={{
          position: isMobile ? "fixed" : "absolute",
          top: position.top,
          left: position.left,
          transform: [
            side === "top" ? "translateY(-100%)" : undefined,
            align === "end" ? "translateX(-100%)" : align === "center" ? "translateX(-50%)" : undefined,
          ].filter(Boolean).join(" ") || undefined,
          ...(isMobile ? { touchAction: "manipulation" } : undefined),
        }}
        className={cn(
          "z-50 min-w-[8rem] overflow-hidden rounded-xl border border-border bg-popover p-1 text-popover-foreground",
          "animate-in fade-in-0 zoom-in-95",
          isMobile && "min-w-[12rem] p-1.5",
          className
        )}
        role="menu"
        onKeyDown={handleKeyDown}
        {...props}
      >
        {children}
      </div>,
      document.body
    );
  }
);
DropdownMenuContent.displayName = "DropdownMenuContent";

interface DropdownMenuItemProps extends React.HTMLAttributes<HTMLDivElement> {
  asChild?: boolean;
}

const DropdownMenuItem = React.forwardRef<HTMLDivElement, DropdownMenuItemProps>(
  ({ className, asChild, children, onClick, ...props }, ref) => {
    const { setOpen, isMobile } = useDropdownMenu();

    const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
      onClick?.(e);
      setOpen(false);
    };

    const itemClasses = cn(
      "relative flex cursor-pointer select-none items-center rounded-lg text-sm outline-none transition-colors hover:bg-muted/50 focus:bg-muted/50",
      isMobile ? "min-h-[44px] px-4 py-3 text-[0.9375rem]" : "px-2 py-1.5",
      className
    );

    if (asChild && React.isValidElement(children)) {
      const childProps = (children as React.ReactElement<React.HTMLAttributes<HTMLElement>>).props;
      const childOnClick = childProps.onClick;
      return React.cloneElement(
        children as React.ReactElement<React.HTMLAttributes<HTMLElement>>,
        {
          className: itemClasses,
          onClick: (e: React.MouseEvent<HTMLElement>) => {
            childOnClick?.(e);
            onClick?.(e as React.MouseEvent<HTMLDivElement>);
            setOpen(false);
          },
          role: "menuitem",
          tabIndex: 0,
        }
      );
    }

    return (
      <div
        ref={ref}
        className={itemClasses}
        onClick={handleClick}
        role="menuitem"
        tabIndex={0}
        style={isMobile ? { touchAction: "manipulation" } : undefined}
        {...props}
      >
        {children}
      </div>
    );
  }
);
DropdownMenuItem.displayName = "DropdownMenuItem";

const DropdownMenuSeparator = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("-mx-1 my-1 h-px bg-muted", className)} {...props} />
));
DropdownMenuSeparator.displayName = "DropdownMenuSeparator";

// Submenu wrapper
const DropdownMenuSub = ({ children }: { children: React.ReactNode }) => {
  const [open, setOpen] = React.useState(false);
  const triggerRef = React.useRef<HTMLElement | null>(null);
  const closeTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  // Immediate close for click actions
  const closeImmediately = React.useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setOpen(false);
  }, []);

  // Delayed close to allow moving between trigger and content
  const handleSetOpen: React.Dispatch<React.SetStateAction<boolean>> = (value) => {
    const nextValue = typeof value === "function" ? value(open) : value;
    
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }

    if (nextValue) {
      setOpen(true);
    } else {
      // Delay close to allow mouse to move to submenu
      closeTimeoutRef.current = setTimeout(() => {
        setOpen(false);
      }, 100);
    }
  };

  React.useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  return (
    <DropdownMenuSubContext.Provider value={{ open, setOpen: handleSetOpen, closeImmediately, triggerRef }}>
      <div className="relative">{children}</div>
    </DropdownMenuSubContext.Provider>
  );
};

// Submenu trigger
interface DropdownMenuSubTriggerProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

const DropdownMenuSubTrigger = React.forwardRef<HTMLDivElement, DropdownMenuSubTriggerProps>(
  ({ className, children, ...props }, ref) => {
    const { setOpen, triggerRef } = useDropdownMenuSub();
    const { isMobile } = useDropdownMenu();

    const handleClick = React.useCallback(() => {
      if (isMobile) {
        setOpen((prev: boolean) => !prev);
      }
    }, [isMobile, setOpen]);

    return (
      <div
        ref={(node) => {
          triggerRef.current = node;
          if (typeof ref === "function") {
            ref(node);
          } else if (ref) {
            ref.current = node;
          }
        }}
        role="menuitem"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpen((prev: boolean) => !prev);
          }
        }}
        className={cn(
          "relative flex cursor-pointer select-none items-center rounded-lg text-sm outline-none transition-colors hover:bg-muted/50 focus:bg-muted/50",
          isMobile ? "min-h-[44px] px-4 py-3 text-[0.9375rem]" : "px-2 py-1.5",
          className
        )}
        onClick={handleClick}
        onMouseEnter={isMobile ? undefined : () => { setOpen(true); }}
        onMouseLeave={isMobile ? undefined : () => { setOpen(false); }}
        style={isMobile ? { touchAction: "manipulation" } : undefined}
        {...props}
      >
        {children}
        <ChevronRight className="ml-auto h-4 w-4" />
      </div>
    );
  }
);
DropdownMenuSubTrigger.displayName = "DropdownMenuSubTrigger";

// Submenu content
interface DropdownMenuSubContentProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

const DropdownMenuSubContent = React.forwardRef<HTMLDivElement, DropdownMenuSubContentProps>(
  ({ className, children, ...props }, ref) => {
    const { open, setOpen, triggerRef } = useDropdownMenuSub();
    const { registerSubContent, unregisterSubContent, isMobile } = useDropdownMenu();
    const contentRef = React.useRef<HTMLDivElement>(null);
    const [position, setPosition] = React.useState<{ top: number; left: number }>({ top: 0, left: 0 });

    // Register this submenu content with parent so click-outside works
    React.useEffect(() => {
      registerSubContent(contentRef);
      return () => {
        unregisterSubContent(contentRef);
      };
    }, [registerSubContent, unregisterSubContent]);

    React.useEffect(() => {
      if (open && triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        if (isMobile) {
          // Fixed positioning — viewport coords
          setPosition({
            top: rect.bottom + 2,
            left: rect.left,
          });
        } else {
          setPosition({
            top: rect.top + window.scrollY,
            left: rect.right + window.scrollX + 4,
          });
        }
      }
    }, [open, triggerRef, isMobile]);

    if (!open) return null;

    return createPortal(
      <div
        ref={(node) => {
          contentRef.current = node;
          if (typeof ref === "function") {
            ref(node);
          } else if (ref) {
            ref.current = node;
          }
        }}
        style={{
          position: isMobile ? "fixed" : "absolute",
          top: position.top,
          left: position.left,
          ...(isMobile ? { touchAction: "manipulation" } : undefined),
        }}
        className={cn(
          "z-[51] min-w-[8rem] overflow-hidden rounded-xl border border-border bg-popover p-1 text-popover-foreground",
          "animate-in fade-in-0 zoom-in-95",
          isMobile && "min-w-[12rem] p-1.5",
          className
        )}
        onMouseEnter={isMobile ? undefined : () => { setOpen(true); }}
        onMouseLeave={isMobile ? undefined : () => { setOpen(false); }}
        {...props}
      >
        {children}
      </div>,
      document.body
    );
  }
);
DropdownMenuSubContent.displayName = "DropdownMenuSubContent";

// Submenu item (closes parent menu on click)
const DropdownMenuSubItem = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, children, onClick, ...props }, ref) => {
    const parentContext = useDropdownMenu();
    const subContext = useDropdownMenuSub();

    const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
      onClick?.(e);
      subContext.closeImmediately();
      parentContext.setOpen(false);
    };

    return (
      <div
        ref={ref}
        className={cn(
          "relative flex cursor-pointer select-none items-center rounded-lg text-sm outline-none transition-colors hover:bg-white/10 focus:bg-white/10",
          parentContext.isMobile ? "min-h-[44px] px-4 py-3 text-[0.9375rem]" : "px-2 py-1.5",
          className
        )}
        onClick={handleClick}
        style={parentContext.isMobile ? { touchAction: "manipulation" } : undefined}
        {...props}
      >
        {children}
      </div>
    );
  }
);
DropdownMenuSubItem.displayName = "DropdownMenuSubItem";

export {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuSubItem,
};
